/**
 * PRP-INTAKE-1 (I2): the estimate page's two tabs.
 *
 * "Estimate" (default) holds the open-items banner and the estimate itself:
 * rooms, headings, lines. "Build list" holds the chat change log (the Chat
 * changes panel) and shows only when the estimate has one. Changing the view
 * mode (`resetKey`) returns to the Estimate tab, so List/Tree/Review always
 * show what they name.
 */
import { useEffect, useId, useState } from 'react';
import type { ReactNode } from 'react';

import { cn } from '../../lib/utils';
import OpenItemsBanner from './OpenItemsBanner';

type Tab = 'estimate' | 'build';

/** Props for {@link EstimateTabs}. */
export interface EstimateTabsProps {
  estimateId: string;
  /** The Build list content (the chat panel), or null when there is none. */
  buildList: ReactNode | null;
  /** Re-fetch the estimate after an open item was answered. */
  onEstimateChanged?: () => void | Promise<void>;
  /** The estimate's payload version: open items refetch when it moves. */
  version?: string;
  /** Switching back to the Estimate tab whenever this changes (the view mode). */
  resetKey?: string;
  /** The estimate content. */
  children: ReactNode;
}

export default function EstimateTabs({
  estimateId,
  buildList,
  onEstimateChanged,
  version,
  resetKey,
  children,
}: EstimateTabsProps) {
  const [tab, setTab] = useState<Tab>('estimate');
  const id = useId();
  useEffect(() => setTab('estimate'), [resetKey]);
  const active: Tab = buildList ? tab : 'estimate';

  const tabButton = (value: Tab, label: string) => (
    <button
      type="button"
      role="tab"
      id={`${id}-${value}`}
      aria-selected={active === value}
      aria-controls={`${id}-${value}-panel`}
      tabIndex={active === value ? 0 : -1}
      onClick={() => setTab(value)}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          setTab(value === 'estimate' ? 'build' : 'estimate');
        }
      }}
      className={cn(
        'h-8 px-3 text-[12.5px] border-b-2 -mb-px',
        active === value
          ? 'border-[var(--accent)] text-[var(--tx-hi)] font-[550]'
          : 'border-transparent text-[var(--tx-lo)] hover:text-[var(--tx-mid)]',
      )}
    >
      {label}
    </button>
  );

  return (
    <>
      {buildList && (
        <div role="tablist" aria-label="Estimate views" className="flex gap-1 mb-3 border-b border-[var(--bd-subtle)]">
          {tabButton('estimate', 'Estimate')}
          {tabButton('build', 'Build list')}
        </div>
      )}
      <div
        role={buildList ? 'tabpanel' : undefined}
        id={`${id}-${active}-panel`}
        aria-labelledby={buildList ? `${id}-${active}` : undefined}
      >
        {active === 'estimate' ? (
          <>
            <OpenItemsBanner
              estimateId={estimateId}
              onChanged={onEstimateChanged}
              refreshKey={version}
            />
            {children}
          </>
        ) : (
          buildList
        )}
      </div>
    </>
  );
}
